/**
 * The single source of truth for bulletin categories.
 *
 * A post stores its category as a plain string in its own database row — it does
 * not point at a shared list. So renaming a category does NOT rename it on the
 * posts already filed under the old name. Rather than rewrite live data, we keep
 * the old value in the database and translate it on read (see CATEGORY_ALIASES).
 *
 * Anything that reads `post.category` must run it through `normalizeCategory()`
 * first. Comparing the raw value is what silently orphans old posts.
 *
 * Keep CATEGORIES in sync with VALID_CATEGORIES in the backend's
 * api/bulletin/bulletin_post/post.py and put.py — the backend coerces any value
 * it does not recognize to "other".
 */

// Canonical values. These are what NEW posts are stored as.
export const CATEGORIES = [
  "announcements",
  "events",
  "help-wanted",
  "memories",
  "resources",
  "connections",
  "in search of",
  "giveaway",
  "opportunities",
  "other",
];

// Values that still exist on older rows, mapped to their canonical replacement.
// "coffee & catch-up" was renamed to "connections" at the 2026-07-09 meeting;
// "formal discussions" was an earlier name for the same category.
export const CATEGORY_ALIASES = {
  "formal discussions": "connections",
  "coffee & catch-up": "connections",
};

/** Translate any stored category value into its canonical form. */
export function normalizeCategory(raw) {
  if (!raw) return "other";
  const canonical = CATEGORY_ALIASES[raw] || raw;
  return CATEGORIES.includes(canonical) ? canonical : "other";
}

// Display names. Derived capitalization would give us "In search of" and
// "Help-wanted", so the labels are spelled out.
export const CATEGORY_LABELS = {
  announcements: "Announcements",
  events: "Events",
  "help-wanted": "Help Wanted",
  memories: "Memories",
  resources: "Resources",
  connections: "Connections",
  "in search of": "In Search Of",
  giveaway: "Giveaway",
  opportunities: "Opportunities",
  other: "Other",
};

/** The label to show for any stored category value, old or new. */
export function categoryLabel(raw) {
  return CATEGORY_LABELS[normalizeCategory(raw)];
}

export const CATEGORY_COLORS = {
  announcements: { bg: "#DBEAFE", color: "#1E40AF" },
  events: { bg: "#FEF3C7", color: "#92400E" },
  "help-wanted": { bg: "#FCE7F3", color: "#9D174D" },
  memories: { bg: "#EDE9FE", color: "#8458D6" },
  resources: { bg: "#D1FAE5", color: "#065F46" },
  // Inherited from "coffee & catch-up" so renamed posts keep their familiar colour.
  connections: { bg: "#F3E8D6", color: "#7C4A1E" },
  "in search of": { bg: "#E0F2FE", color: "#075985" },
  giveaway: { bg: "#FAE8FF", color: "#A21CAF" },
  opportunities: { bg: "#FFE3C9", color: "#C2410C" },
  other: { bg: "#D9DDE3", color: "#3A4250" },
};
